import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { RemovingThrobber } from "../../removing_throbber";

export function EventTemplateList(props: {
	itemCreator: (
		item: RouterInferenceHelpers["event"]["list"]["output"]["items"][0]
	) => JSX.Element;
	list_query: {
		hasNextPage: boolean;
		isFetching: boolean;
		isFetchingNextPage: boolean;
		isLoading: boolean;
		isRefetching: boolean;
		fetchNextPage: () => void;
		data: {
			pages: {
				items: RouterInferenceHelpers["event"]["list"]["output"]["items"];
			}[];
		};
	};
}) {
	const { ref, inView } = useInView();
	const {
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
		isFetching,
		isLoading,
		isRefetching,
	} = props.list_query;
	const items =
		props.list_query.data?.pages.flatMap((val) => val.items) ?? [];

	const parentRef = useRef();

	const virtualizer = useVirtualizer({
		count: hasNextPage ? items.length + 1 : items.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 64,
		overscan: 3,
	});

	useEffect(() => {
		if (inView) {
			fetchNextPage();
		}
	}, [inView]);

	// const is_initial_fetch = !(!isRefetching || isFetchingNextPage);
	const is_initial_fetch = isLoading || (isRefetching && !isFetchingNextPage);

	if (is_initial_fetch) {
		return (
			<div className="">
				<PlaceholderRow>
					<div className="flex  items-center gap-2">
						Loading <RemovingThrobber />
					</div>
				</PlaceholderRow>
			</div>
		);
	}

	if (!isFetching && items.length === 0) {
		return (
			<PlaceholderRow>
				<div className="w-full px-4 text-left text-content-secondary">
					No events found
				</div>
			</PlaceholderRow>
		);
	}

	return (
		<div ref={parentRef} className="w-full overflow-auto">
			<div
				style={{
					height: `${virtualizer.getTotalSize()}px`,
					width: "100%",
					position: "relative",
				}}
			>
				{virtualizer.getVirtualItems().map((virtualRow) => {
					const isLoaderRow = virtualRow.index > items.length - 1;

					const post = items[virtualRow.index];

					return (
						<div
							key={virtualRow.index}
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								transform: `translateY(${virtualRow.start}px)`,
							}}
						>
							{isLoaderRow ? (
								<PlaceholderRow>
									<div
										ref={ref}
										className="flex h-full items-center gap-2"
									>
										Loading <RemovingThrobber />
									</div>
								</PlaceholderRow>
							) : (
								<div
									data-index={virtualRow.index}
									ref={virtualizer.measureElement}
								>
									<div className="pb-1">
										{props.itemCreator(post)}
									</div>
									{!hasNextPage &&
										virtualRow.index ===
											items.length - 1 && (
											<PlaceholderRow>
												End of Events
											</PlaceholderRow>
										)}
								</div>
							)}
						</div>
					);
				})}
				{!isFetching && items.length === 0 && (
					<PlaceholderRow>End of Events</PlaceholderRow>
				)}
			</div>
		</div>
	);
}

export function PlaceholderRow(props: { children: React.ReactNode }) {
	return (
		<div className="">
			<div className="flex flex-col items-center justify-center gap-0 border border-base-light bg-base-dark py-3 text-sesh-dark">
				{props.children}
			</div>
		</div>
	);
}
